import { useRef, useState, type InputHTMLAttributes } from 'react'
import { useLiveRoom } from '../LiveRoom'

// Finish updating linked mentions after a rename. Intermediate prefixes while
// typing must not capture another entry's mentions.
export function ReferenceNameInput({
  value,
  onRename,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  value: string
  onRename: (name: string) => void
}) {
  const room = useLiveRoom()
  const path = (props as Record<string, unknown>)['data-live-path'] as string | undefined
  const live = path ? room?.document.current : null
  const original = useRef<string | null>(null)
  const [draft, setDraft] = useState<{ base: string; text: string } | null>(
    null,
  )
  const cancelled = useRef(false)
  const text = live ? value : draft?.base === value ? draft.text : value
  return (
    <input
      {...props}
      value={text}
      title={live ? 'Names merge live. Press Enter or leave the field to update linked mentions.' : 'Press Enter or leave this field to rename. Escape cancels.'}
      onFocus={event => { original.current = value; live?.undo.stopCapturing(); props.onFocus?.(event) }}
      onChange={(event) => {
        if (live && path) live.changeField(JSON.parse(path), event.target.value)
        else setDraft({ base: value, text: event.target.value })
      }}
      onBlur={(event) => {
        if (live && path) {
          if (original.current !== null) live.finishRename(JSON.parse(path), original.current)
          original.current = null; live.undo.stopCapturing(); props.onBlur?.(event); return
        }
        if (!cancelled.current && text.trim() && text.trim() !== value)
          onRename(text.trim())
        cancelled.current = false
        setDraft(null)
        props.onBlur?.(event)
      }}
      onKeyDown={(event) => {
        if (
          (event.metaKey || event.ctrlKey) &&
          event.key.toLowerCase() === 's'
        ) {
          event.currentTarget.blur()
          props.onKeyDown?.(event)
          return
        }
        if (event.key === 'Escape' && (!draft || text === value)) {
          props.onKeyDown?.(event)
          return
        }
        if (event.key === 'Enter' || event.key === 'Escape') {
          event.preventDefault()
          event.stopPropagation()
          cancelled.current = event.key === 'Escape'
          event.currentTarget.blur()
        } else props.onKeyDown?.(event)
      }}
    />
  )
}
